"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/store/cart";
import { useAssociationStore } from "@/store/association";
import { getAssociationId } from "@/config/association";
import { useAssociationShippingCost } from "@/lib/useAssociationShippingCost";
import { usePortalFeatures } from "@/lib/portalFeatures";
import { Check, MinusCircle, PlusCircle, Trash2 } from "lucide-react";
import {
  CircleDollarToSlotIcon,
  SarIcon,
  ShoppingBagIcon,
} from "@/components/icons";
import { AutoFallbackImage } from "@/components/ui/auto-fallback-image";
import { Icon } from "@iconify/react";
import { clientGet } from "@/lib/api/clientApi";
import type { Category, ProjectShare } from "@/types";
import type { PaginatedResponse } from "@/types";
import {
  transformCategories,
  transformProjectShares,
} from "@/lib/api/transformers";
import { formatShortDate } from "@/lib/utils";
import { ApplePayCheckout } from "@/components/checkout/ApplePayCheckout";
import { UnifiedDonationModal } from "@/components/checkout/UnifiedDonationModal";
import { useCartCheckout } from "@/components/cart/useCartCheckout";
import { EmptyCart } from "./EmptyCart";
import {
  normalizeFlag,
  cartRequiresTokenizedPayment,
  isInstallmentLike,
  formatAspAmount,
  resolveCartProjectImage,
  resolveCartProjectName,
  resolveCartItemImage,
  resolveCartItemName,
  periodTypeKey,
} from "./cartUtils";
import { useCartProjectMeta } from "./useCartProjectMeta";
import { PhoneInput } from "@/components/ui/phone-input";
import { cleanPhoneNumber, getMobileDigitCount } from "@/lib/utils/phone";
import type { Value } from "react-phone-number-input";

const formatCartDate = (value: unknown, locale: string): string => {
  return formatShortDate(value, locale);
};

/** Cart page section title bar (dark blue band, matches donation basket mockup). */
const cartSectionBarClassName =
  "bg-primary text-primary-foreground font-bold px-4 py-3.5 text-start text-base sm:text-lg";

/** White card + light border for cart tables / panels. */
const cartPanelClassName =
  "overflow-hidden bg-white rounded-lg sm:rounded-xl border border-[#e0e0e0]";

export function CartPageContent() {
  const t = useTranslations();
  const tDeductions = useTranslations("profile.deductions");
  const locale = useLocale();
  const shippingProductsValue = useAssociationShippingCost();
  const portalFeatures = usePortalFeatures();
  const {
    items,
    removeItem,
    updateQuantity: storeUpdateQuantity,
    updateAmount,
    getTotal,
    loadCart,
    isHydrated,
    isLoading,
  } = useCartStore();

  const total = getTotal();

  const [pendingAmounts, setPendingAmounts] = useState<Record<number, string>>(
    {},
  );
  const {
    donationModalOpen,
    donationModalIntent,
    handleCloseDonationModal,
    handleCompleteDonation,
    handlePaymentSuccess,
  } = useCartCheckout();
  const storeAssociationId = useAssociationStore((s) => s.associationId);

  // Inline guest phone collection for Apple Pay (avoids redundant full payment modal)
  const [guestPhone, setGuestPhone] = useState("");
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const applePayTriggerRef = useRef<(() => void) | null>(null);

  const handleGuestPhoneSubmit = useCallback(() => {
    if (!guestPhone.trim()) return;
    if (getMobileDigitCount(cleanPhoneNumber(guestPhone)) < 9) {
      setPhoneError(t("auth.mobileRequired"));
      return;
    }
    setPhoneError("");
    setShowPhonePrompt(false);
    requestAnimationFrame(() => {
      applePayTriggerRef.current?.();
    });
  }, [guestPhone, t]);

  // ─── Optimistic quantity + debounce (mirrors CartDrawer) ──────────────────
  const DEBOUNCE_MS = 500;
  const [optimisticQtys, setOptimisticQtys] = useState<Record<number, number>>(
    {},
  );
  const [busyItemIds, setBusyItemIds] = useState<Set<number>>(new Set());
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>(
    {},
  );

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    setOptimisticQtys((prev) => {
      const next: Record<number, number> = {};
      let changed = false;
      for (const [idStr, qty] of Object.entries(prev)) {
        const id = Number(idStr);
        const serverItem = items.find((i) => i.Id === id);
        if (serverItem && serverItem.Quantity !== qty && !busyItemIds.has(id)) {
          changed = true;
        } else if (serverItem) {
          next[id] = qty;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [items, busyItemIds]);

  const debouncedUpdateQuantity = useCallback(
    (id: number, newQty: number) => {
      if (newQty < 1) return;
      setOptimisticQtys((prev) => ({ ...prev, [id]: newQty }));

      if (debounceTimers.current[id]) {
        clearTimeout(debounceTimers.current[id]);
      }

      debounceTimers.current[id] = setTimeout(async () => {
        delete debounceTimers.current[id];
        setBusyItemIds((prev) => new Set(prev).add(id));
        try {
          const result = await storeUpdateQuantity(id, newQty);
          if (!result.success) {
            setOptimisticQtys((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
          }
        } finally {
          setBusyItemIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setOptimisticQtys((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        }
      }, DEBOUNCE_MS);
    },
    [storeUpdateQuantity],
  );

  const handleUpdateAmount = useCallback(
    async (id: number, amount: number) => {
      if (amount <= 0) return;
      setBusyItemIds((prev) => new Set(prev).add(id));
      try {
        await updateAmount(id, amount);
        setPendingAmounts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } finally {
        setBusyItemIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [updateAmount],
  );
  const associationId =
    storeAssociationId && storeAssociationId > 0
      ? storeAssociationId
      : getAssociationId();

  // ─── Quick-add upsell ────────────────────────────────────────────────────
  const addItem = useCartStore((s) => s.addItem);
  const [quickProjectId, setQuickProjectId] = useState<number | null>(null);
  const [quickCategories, setQuickCategories] = useState<Category[]>([]);
  const [quickCategoryId, setQuickCategoryId] = useState<string>("");
  const [quickProjectName, setQuickProjectName] = useState<string>("");
  const [isQuickAdding, setIsQuickAdding] = useState(false);
  const quickAmounts = [5, 10, 20];

  useEffect(() => {
    if (!associationId) return;
    let cancelled = false;
    void (async () => {
      try {
        const [catResp, projResp] = await Promise.all([
          clientGet<{ data?: Category[] }>("/api/categories", {
            associationId,
          }),
          clientGet<PaginatedResponse<{ Id?: number; Name?: string }>>(
            "/api/projects",
            {
              associationId,
              isActive: "true",
              isProduct: "false",
              page: 1,
              pageSize: 1,
            },
          ),
        ]);
        if (cancelled) return;
        const cats = transformCategories(
          catResp.data || [],
          locale as "ar" | "en",
        );
        setQuickCategories(cats);
        const firstCat = cats[0];
        if (firstCat) setQuickCategoryId(String(firstCat.Id));
        const first = projResp.data?.[0];
        if (first?.Id) {
          setQuickProjectId(first.Id);
          setQuickProjectName(
            typeof first.Name === "string"
              ? first.Name
              : t("quickDonation.title"),
          );
        } else {
          setQuickProjectId(null);
        }
      } catch {
        if (!cancelled) {
          setQuickProjectId(null);
          setQuickCategories([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [associationId, locale, t]);

  const handleQuickAdd = async (amount: number) => {
    if (!quickProjectId || !quickCategoryId || amount <= 0) return;
    setIsQuickAdding(true);
    try {
      const existing = items.find(
        (i) =>
          i.ProjectId === quickProjectId &&
          i.Amount === amount &&
          i.DonateTypeId === 5 &&
          !normalizeFlag(i.IsProduct) &&
          !normalizeFlag(i.GiftProject) &&
          !i.ProjectShareId,
      );
      if (existing) {
        await storeUpdateQuantity(existing.Id, existing.Quantity + 1);
      } else {
        await addItem({
          ProjectId: quickProjectId,
          Amount: amount,
          Quantity: 1,
          DonateTypeId: 5,
          CategoryId: parseInt(quickCategoryId, 10),
          DonationOpen: false,
          IsProduct: false,
          GiftProject: false,
          ProjectName: quickProjectName,
          ProjectImage: null,
          ProjectShareId: null,
        });
      }
    } finally {
      setIsQuickAdding(false);
    }
  };

  const supportDonationItems = useMemo(
    () =>
      items.filter(
        (i) =>
          !normalizeFlag(i.IsProduct) &&
          !normalizeFlag(i.GiftProject) &&
          !isInstallmentLike(i.DonateTypeId, i.isRecurring),
      ),
    [items],
  );

  const installmentItems = useMemo(
    () =>
      items.filter(
        (i) =>
          !normalizeFlag(i.IsProduct) &&
          !normalizeFlag(i.GiftProject) &&
          isInstallmentLike(i.DonateTypeId, i.isRecurring),
      ),
    [items],
  );

  const giftItems = useMemo(
    () => items.filter((i) => normalizeFlag(i.GiftProject)),
    [items],
  );

  const requiresTokenizedPayment = useMemo(
    () => cartRequiresTokenizedPayment(items),
    [items],
  );

  const productItems = useMemo(
    () => items.filter((i) => normalizeFlag(i.IsProduct)),
    [items],
  );
  const hasProductItems = productItems.length > 0;
  const shippingProducts =
    productItems.length > 0 && shippingProductsValue > 0
      ? shippingProductsValue
      : 0;
  const grandTotal = total + shippingProducts;
  const minTotalCart = portalFeatures.minTotalCart;
  const belowMinTotal =
    minTotalCart != null && minTotalCart > 0 && grandTotal < minTotalCart;
  // Map ProjectShareId -> ShareName (per project) so cart shows share name instead of ID
  const projectMetaById = useCartProjectMeta(items, locale as "ar" | "en");

  const fetchedProjectSharesRef = useRef<Set<number>>(new Set());
  const [shareNameByKey, setShareNameByKey] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    const projectIdsToFetch = Array.from(
      new Set(
        items
          .filter((i) => i.IsProduct !== true)
          .filter((i) => i.ProjectShareId != null && i.ProjectShareId > 0)
          .map((i) => i.ProjectId),
      ),
    ).filter((pid) => !fetchedProjectSharesRef.current.has(pid));

    if (projectIdsToFetch.length === 0) return;
    let cancelled = false;

    void Promise.allSettled(
      projectIdsToFetch.map(async (projectId) => {
        const resp = await clientGet<{ data?: { shares?: ProjectShare[] } }>(
          `/api/projects/${projectId}/shares`,
        );
        const data = resp.data;
        const shares = transformProjectShares(
          data?.shares ?? [],
          locale as "ar" | "en",
        );
        return { projectId, shares };
      }),
    ).then((results) => {
      if (cancelled) return;

      const updates: Record<string, string> = {};
      for (const r of results) {
        if (r.status !== "fulfilled") continue;
        fetchedProjectSharesRef.current.add(r.value.projectId);
        for (const s of r.value.shares) {
          updates[`${r.value.projectId}:${s.Id}`] = s.ShareName;
        }
      }
      if (Object.keys(updates).length > 0) {
        setShareNameByKey((prev) => ({ ...prev, ...updates }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [items, locale]);

  useEffect(() => {
    void loadCart();
  }, [loadCart]);

  // Do NOT call processCart here on page load. Backend POST /payments/process-cart starts checkout
  // from the server cart and clears it; pre-calling would empty the DB cart while the user is
  // still on the cart page. UnifiedDonationModal runs the same flow when they open checkout.

  // Only show full skeleton on initial hydration, not on subsequent quantity/amount updates
  if (!isHydrated) {
    return (
      <div className="min-h-screen w-full">
        <div className="content-container">
          <div className="min-h-[50vh] sm:min-h-[60vh] flex items-center justify-center py-6 sm:py-10">
            <div className="text-center px-2">
              <div
                className="mx-auto w-[140px] sm:w-[180px] h-[120px] sm:h-[140px] rounded-xl bg-gray-100 animate-pulse"
                aria-hidden
              />
              <p className="mt-3 sm:mt-4 text-base sm:text-xl text-muted-foreground">
                {t("cart.loading")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen w-full ">
        <div className="content-container">
          <div className="min-h-[50vh] sm:min-h-[60vh] flex items-center justify-center py-6 sm:py-10">
            <EmptyCart />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full">
      <div className="content-container py-4 sm:py-6 md:py-8">
        {/* Title row */}
        <div className="relative overflow-hidden mb-3">
          {isLoading && (
            <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden z-10">
              <div className="animate-loading-bar bg-primary/60 rounded-full" />
            </div>
          )}
          <div className="flex items-center gap-2 px-2 sm:px-6 md:px-10 py-2 sm:py-4">
            <ShoppingBagIcon className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground shrink-0" />

            <h1 className="text-lg sm:text-xl font-extrabold text-gray-800">
              {t("cart.title")}
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(280px,380px)] gap-6 xl:gap-8 lg:items-start px-2 sm:px-6 md:px-10">
          <div className="space-y-6 sm:space-y-8 min-w-0">
            {/* Support projects section */}
            {supportDonationItems.length > 0 && (
              <div className={cartPanelClassName}>
                <div className={cartSectionBarClassName}>
                  {t("cart.sections.supportProjects")}
                </div>
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <table className="w-full min-w-[640px] sm:min-w-[780px] table-fixed">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr className="text-xs sm:text-sm">
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[64px] sm:w-[80px]">
                          {t("cart.table.image")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center">
                          {t("cart.table.project")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center whitespace-nowrap">
                          {t("cart.table.amount")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[100px] sm:w-[140px]">
                          {t("cart.table.quantity")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center whitespace-nowrap">
                          {t("cart.table.total")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[56px] sm:w-[72px]">
                          {t("cart.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportDonationItems.map((item) => {
                        const showAmountEditor =
                          item.DonationOpen === true || item.DonateTypeId === 4;
                        const pending = pendingAmounts[item.Id];
                        const currentAmount =
                          pending != null ? pending : String(item.Amount ?? 0);
                        const {
                          imageUrl: projectImageUrl,
                          imageId: projectImageId,
                        } = resolveCartItemImage(item, locale, projectMetaById);
                        const projectName = resolveCartItemName(
                          item,
                          locale,
                          projectMetaById,
                          (id) => t("cart.fallbackProject", { id }),
                          () => t("cart.fallbackDeduction"),
                        );
                        const shareLabel =
                          item.ProjectShareId && item.ProjectShareId > 0
                            ? shareNameByKey[
                                `${item.ProjectId}:${item.ProjectShareId}`
                              ] || `#${item.ProjectShareId}`
                            : null;
                        const displayQty =
                          optimisticQtys[item.Id] ?? item.Quantity;
                        const itemBusy = busyItemIds.has(item.Id);
                        return (
                          <tr
                            key={item.Id}
                            className="border-t transition-opacity duration-200"
                            style={{ opacity: itemBusy ? 0.6 : 1 }}
                          >
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <div className="w-10 h-10 sm:w-[56px] sm:h-[56px] rounded-lg overflow-hidden bg-gray-100 mx-auto">
                                <AutoFallbackImage
                                  imageUrl={projectImageUrl}
                                  imageId={projectImageId}
                                  alt={projectName}
                                  width={80}
                                  height={80}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold text-center align-middle text-sm sm:text-base">
                              <div>{projectName}</div>
                              {shareLabel ? (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {shareLabel}
                                </div>
                              ) : null}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {formatAspAmount(item.Amount, locale)}{" "}
                              <SarIcon className="mx-1" />
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              {showAmountEditor ? (
                                <div className="flex items-center justify-center">
                                  <div className="flex items-center rounded-xl border bg-white overflow-visible">
                                    <input
                                      type="number"
                                      className="h-10 sm:h-11 min-w-[5rem] sm:min-w-[7rem] w-28 sm:w-40 py-2 sm:py-3 ps-6 sm:ps-8 pe-2 sm:pe-3 text-right text-sm sm:text-base font-semibold outline-none"
                                      value={currentAmount}
                                      min={1}
                                      onChange={(e) =>
                                        setPendingAmounts((p) => ({
                                          ...p,
                                          [item.Id]: e.target.value,
                                        }))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          void handleUpdateAmount(
                                            item.Id,
                                            Number(currentAmount) || 0,
                                          );
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      className="h-11 w-12 flex items-center justify-center border-s rounded-l-none ltr:rounded-r-xl rtl:rounded-l-xl bg-white hover:bg-gray-50 shrink-0 disabled:opacity-50"
                                      disabled={itemBusy}
                                      onClick={() =>
                                        void handleUpdateAmount(
                                          item.Id,
                                          Number(currentAmount) || 0,
                                        )
                                      }
                                      aria-label="apply-amount"
                                    >
                                      <Check className="h-5 w-5 text-success" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="flex items-center justify-between inline-flex"
                                  style={{
                                    backgroundColor: "#f8f8f8",
                                    borderRadius: "var(--te-radius, 10px)",
                                    padding: "8px 12px",
                                    maxWidth: 220,
                                  }}
                                >
                                  <MinusCircle
                                    onClick={() =>
                                      displayQty > 1 &&
                                      debouncedUpdateQuantity(
                                        item.Id,
                                        displayQty - 1,
                                      )
                                    }
                                    style={{
                                      height: "20px",
                                      width: "20px",
                                      cursor:
                                        displayQty <= 1
                                          ? "not-allowed"
                                          : "pointer",
                                      opacity: displayQty <= 1 ? 0.5 : 1,
                                    }}
                                    color="var(--association-theme-color-3)"
                                  />
                                  <input
                                    type="number"
                                    value={displayQty}
                                    min={1}
                                    className="text-sm font-bold text-gray-800 w-16 text-center bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    onChange={(e) => {
                                      const v = Math.max(
                                        1,
                                        parseInt(e.target.value) || 1,
                                      );
                                      debouncedUpdateQuantity(item.Id, v);
                                    }}
                                  />
                                  <PlusCircle
                                    onClick={() =>
                                      debouncedUpdateQuantity(
                                        item.Id,
                                        displayQty + 1,
                                      )
                                    }
                                    style={{
                                      height: "20px",
                                      width: "20px",
                                      cursor: "pointer",
                                    }}
                                    color="var(--association-theme-color-3)"
                                  />
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {formatAspAmount(
                                item.Amount * displayQty,
                                locale,
                              )}{" "}
                              <SarIcon className="mx-1" />
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <button
                                type="button"
                                onClick={() => removeItem(item.Id)}
                                className="text-gray-700 hover:text-red-600 p-1"
                                aria-label="remove"
                              >
                                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Installments / deductions section */}
            {installmentItems.length > 0 && (
              <div className={cartPanelClassName}>
                <div className={cartSectionBarClassName}>
                  {t("cart.sections.installments")}
                </div>
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <table className="w-full min-w-[700px] sm:min-w-[880px] table-fixed">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr className="text-xs sm:text-sm">
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[64px] sm:w-[80px]">
                          {t("cart.table.image")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center">
                          {t("cart.table.project")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center">
                          {t("cart.table.paymentDate")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center whitespace-nowrap">
                          {t("cart.table.amount")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[100px] sm:w-[140px]">
                          {t("cart.table.quantity")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center whitespace-nowrap">
                          {t("cart.table.total")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[56px] sm:w-[72px]">
                          {t("cart.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {installmentItems.map((item) => {
                        const img = resolveCartItemImage(
                          item,
                          locale,
                          projectMetaById,
                        );
                        const projectName = resolveCartItemName(
                          item,
                          locale,
                          projectMetaById,
                          (id) => t("cart.fallbackProject", { id }),
                          () => t("cart.fallbackDeduction"),
                        );
                        const shareLabel =
                          item.ProjectShareId && item.ProjectShareId > 0
                            ? shareNameByKey[
                                `${item.ProjectId}:${item.ProjectShareId}`
                              ] || `#${item.ProjectShareId}`
                            : null;
                        const displayQty =
                          optimisticQtys[item.Id] ?? item.Quantity;
                        const itemBusy = busyItemIds.has(item.Id);

                        return (
                          <tr
                            key={item.Id}
                            className="border-t transition-opacity duration-200"
                            style={{ opacity: itemBusy ? 0.6 : 1 }}
                          >
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <div className="w-10 h-10 sm:w-[56px] sm:h-[56px] rounded-lg overflow-hidden bg-gray-100 mx-auto">
                                <AutoFallbackImage
                                  imageUrl={img.imageUrl}
                                  imageId={img.imageId}
                                  alt={projectName}
                                  width={80}
                                  height={80}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold text-center align-middle text-sm sm:text-base">
                              <div>{projectName}</div>
                              {shareLabel ? (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {shareLabel}
                                </div>
                              ) : null}
                              {periodTypeKey(item.periodType) ? (
                                <div className="text-xs text-primary mt-1 font-semibold">
                                  {tDeductions(
                                    `frequency.${periodTypeKey(item.periodType)}`,
                                  )}
                                </div>
                              ) : null}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {formatCartDate(item.recurringDate, locale)}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {formatAspAmount(item.Amount, locale)}{" "}
                              <SarIcon className="mx-1" />
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <div
                                className="flex items-center justify-between inline-flex"
                                style={{
                                  backgroundColor: "#f8f8f8",
                                  borderRadius: "var(--te-radius, 10px)",
                                  padding: "6px 10px",
                                  maxWidth: 220,
                                }}
                              >
                                <MinusCircle
                                  onClick={() =>
                                    displayQty > 1 &&
                                    debouncedUpdateQuantity(
                                      item.Id,
                                      displayQty - 1,
                                    )
                                  }
                                  style={{
                                    height: "20px",
                                    width: "20px",
                                    cursor:
                                      displayQty <= 1
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity: displayQty <= 1 ? 0.5 : 1,
                                  }}
                                  color="var(--association-theme-color-3)"
                                />
                                <input
                                  type="number"
                                  value={displayQty}
                                  min={1}
                                  className="text-sm font-bold text-gray-800 w-16 text-center bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  onChange={(e) => {
                                    const v = Math.max(
                                      1,
                                      parseInt(e.target.value) || 1,
                                    );
                                    debouncedUpdateQuantity(item.Id, v);
                                  }}
                                />
                                <PlusCircle
                                  onClick={() =>
                                    debouncedUpdateQuantity(
                                      item.Id,
                                      displayQty + 1,
                                    )
                                  }
                                  style={{
                                    height: "20px",
                                    width: "20px",
                                    cursor: "pointer",
                                  }}
                                  color="var(--association-theme-color-3)"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {formatAspAmount(
                                item.Amount * displayQty,
                                locale,
                              )}{" "}
                              <SarIcon className="mx-1" />
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <button
                                type="button"
                                onClick={() => removeItem(item.Id)}
                                className="text-gray-700 hover:text-red-600 p-1"
                                aria-label="remove"
                              >
                                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Gifts section */}
            {giftItems.length > 0 && (
              <div className={cartPanelClassName}>
                <div className={cartSectionBarClassName}>
                  {t("cart.sections.gifts")}
                </div>
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <table className="w-full min-w-[720px] sm:min-w-[920px] table-fixed">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr className="text-xs sm:text-sm">
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[64px] sm:w-[80px]">
                          {t("cart.table.image")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center">
                          {t("cart.table.project")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center">
                          {t("cart.table.giftSenderName")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center">
                          {t("cart.table.giftRecipientName")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center">
                          {t("cart.table.giftRecipientMobile")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center whitespace-nowrap">
                          {t("cart.table.amount")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[56px] sm:w-[72px]">
                          {t("cart.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {giftItems.map((item) => {
                        const img = resolveCartItemImage(
                          item,
                          locale,
                          projectMetaById,
                        );
                        const projectName = resolveCartItemName(
                          item,
                          locale,
                          projectMetaById,
                          (id) => t("cart.fallbackProject", { id }),
                          () => t("cart.fallbackDeduction"),
                        );
                        const giftInfo = item.giftInfo as
                          | {
                              senderName?: string;
                              recipientName?: string;
                              recipientMobile?: string;
                            }
                          | undefined;

                        const senderName = giftInfo?.senderName || "-";
                        const recipientName = giftInfo?.recipientName || "-";
                        const recipientMobile =
                          giftInfo?.recipientMobile || "-";

                        const shareLabel =
                          item.ProjectShareId && item.ProjectShareId > 0
                            ? shareNameByKey[
                                `${item.ProjectId}:${item.ProjectShareId}`
                              ] || `#${item.ProjectShareId}`
                            : null;

                        return (
                          <tr key={item.Id} className="border-t">
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <div className="w-10 h-10 sm:w-[56px] sm:h-[56px] rounded-lg overflow-hidden bg-gray-100 mx-auto">
                                <AutoFallbackImage
                                  imageUrl={img.imageUrl}
                                  imageId={img.imageId}
                                  alt={projectName}
                                  width={80}
                                  height={80}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold text-center align-middle text-sm sm:text-base">
                              <div>{projectName}</div>

                              {shareLabel ? (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {shareLabel}
                                </div>
                              ) : null}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {senderName}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {recipientName}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {recipientMobile}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {formatAspAmount(
                                item.Amount * item.Quantity,
                                locale,
                              )}{" "}
                              <SarIcon className="mx-1" />
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <button
                                type="button"
                                onClick={() => removeItem(item.Id)}
                                className="text-gray-700 hover:text-red-600 p-1"
                                aria-label="remove"
                              >
                                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Products section */}
            {productItems.length > 0 && (
              <div className={cartPanelClassName}>
                <div className={cartSectionBarClassName}>
                  {t("cart.sections.products")}
                </div>
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <table className="w-full min-w-[640px] sm:min-w-[780px] table-fixed">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr className="text-xs sm:text-sm">
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[64px] sm:w-[80px]">
                          {t("cart.table.image")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center">
                          {t("cart.table.project")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center whitespace-nowrap">
                          {t("cart.table.amount")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[100px] sm:w-[140px]">
                          {t("cart.table.quantity")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center whitespace-nowrap">
                          {t("cart.table.total")}
                        </th>
                        <th className="py-3 px-2 sm:py-4 sm:px-4 font-semibold text-center w-[56px] sm:w-[72px]">
                          {t("cart.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {productItems.map((item) => {
                        const img = resolveCartProjectImage(
                          item.ProjectImage,
                          locale,
                        );
                        const projectName =
                          resolveCartProjectName(item.ProjectName, locale) ||
                          t("cart.fallbackProduct", {
                            id: item.ProjectId,
                          });
                        const displayQty =
                          optimisticQtys[item.Id] ?? item.Quantity;
                        const itemBusy = busyItemIds.has(item.Id);
                        return (
                          <tr
                            key={item.Id}
                            className="border-t transition-opacity duration-200"
                            style={{ opacity: itemBusy ? 0.6 : 1 }}
                          >
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <div className="w-10 h-10 sm:w-[56px] sm:h-[56px] rounded-lg overflow-hidden bg-gray-100 mx-auto">
                                <AutoFallbackImage
                                  imageUrl={img.imageUrl}
                                  imageId={img.imageId}
                                  alt={projectName}
                                  width={80}
                                  height={80}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold text-center align-middle text-sm sm:text-base">
                              {projectName}
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {formatAspAmount(item.Amount, locale)}{" "}
                              <SarIcon className="mx-1" />
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <div
                                className="flex items-center justify-between inline-flex"
                                style={{
                                  backgroundColor: "#f8f8f8",
                                  borderRadius: "var(--te-radius, 10px)",
                                  padding: "6px 10px",
                                  maxWidth: 220,
                                }}
                              >
                                <MinusCircle
                                  onClick={() =>
                                    displayQty > 1 &&
                                    debouncedUpdateQuantity(
                                      item.Id,
                                      displayQty - 1,
                                    )
                                  }
                                  style={{
                                    height: "20px",
                                    width: "20px",
                                    cursor:
                                      displayQty <= 1
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity: displayQty <= 1 ? 0.5 : 1,
                                  }}
                                  color="var(--association-theme-color-3)"
                                />
                                <div className="text-sm font-bold text-gray-800 w-16 text-center">
                                  {displayQty}
                                </div>
                                <PlusCircle
                                  onClick={() =>
                                    debouncedUpdateQuantity(
                                      item.Id,
                                      displayQty + 1,
                                    )
                                  }
                                  style={{
                                    height: "20px",
                                    width: "20px",
                                    cursor: "pointer",
                                  }}
                                  color="var(--association-theme-color-3)"
                                />
                              </div>
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 font-semibold whitespace-nowrap text-center align-middle text-xs sm:text-base">
                              {formatAspAmount(
                                item.Amount * displayQty,
                                locale,
                              )}{" "}
                              <SarIcon className="mx-1" />
                            </td>
                            <td className="py-3 px-2 sm:py-5 sm:px-4 text-center align-middle">
                              <button
                                type="button"
                                onClick={() => removeItem(item.Id)}
                                className="text-gray-700 hover:text-red-600 p-1"
                                aria-label="remove"
                              >
                                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <aside className="mt-6 lg:mt-0 shrink-0 lg:sticky lg:top-20 xl:top-24">
            <div className="rounded-lg sm:rounded-xl border border-[#e0e0e0] bg-white p-4 sm:p-5 md:p-6 md:pt-2">
              <div className="space-y-4 sm:space-y-5">
                <div className="space-y-3">
                  {shippingProducts > 0 && (
                    <div className="flex items-center justify-between gap-3 text-sm sm:text-base text-gray-600">
                      <span className="text-start">
                        {t("cart.shippingProductsCost")}
                      </span>
                      <span className="font-medium whitespace-nowrap tabular-nums">
                        {formatAspAmount(shippingProducts, locale)}{" "}
                        <SarIcon className="mx-1" />
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-3 pt-2 ">
                    <span className="text-base sm:text-lg font-bold text-gray-800 text-start">
                      {t("cart.grandTotalLabel")}
                    </span>
                    <span className="text-lg sm:text-2xl font-bold text-primary whitespace-nowrap tabular-nums">
                      {formatAspAmount(grandTotal, locale)}{" "}
                      <SarIcon className="mx-1" />
                    </span>
                  </div>
                </div>

                {/* Upsell quick-add */}
                {quickProjectId && quickCategories.length > 0 && (
                  <div className="rounded-2xl border border-sky-100 bg-[#3DB6BA1A] px-4 py-3 dark:bg-sky-950/20">
                    <div className="flex gap-2 align-center">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
                        <Icon
                          icon="material-symbols:psychiatry-outline-rounded"
                          className="size-5"
                          aria-hidden
                        />
                      </div>
                      <p className="min-w-0 flex-1 text-sm font-bold flex items-center text-foreground">
                        (( {t("cart.drawerUpsellHint")} ))
                      </p>
                    </div>
                    <p className="my-3 text-muted-foreground">
                      {t("cart.drawerUpsellBody")}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {quickAmounts.map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          disabled={isQuickAdding || isLoading}
                          onClick={() => void handleQuickAdd(amt)}
                          className="rounded-full border border-secondary text-secondary px-3 py-1.5 text-sm font-semibold shadow-sm transition-colors hover:bg-muted disabled:opacity-50"
                        >
                          <span className="text-sm font-semibold me-2">
                            {t("products.add")}
                          </span>
                          {formatAspAmount(amt, locale)}{" "}
                          <SarIcon className="mx-0.5" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {showPhonePrompt && (
                  <div className="space-y-3 pt-1">
                    <p className="text-sm font-medium text-foreground">
                      {t("auth.mobileRequired")}
                    </p>
                    <PhoneInput
                      value={(guestPhone || "") as Value}
                      onChange={(val) => {
                        setGuestPhone(val ?? "");
                        if (phoneError) setPhoneError("");
                      }}
                      placeholder={t("common.mobile")}
                      className="h-12 rounded-xl bg-white border-gray-100"
                    />
                    {phoneError && (
                      <p className="text-xs text-destructive">{phoneError}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 h-12 rounded-xl"
                        onClick={() => setShowPhonePrompt(false)}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        type="button"
                        className="flex-1 h-12 rounded-xl"
                        onClick={handleGuestPhoneSubmit}
                        disabled={!guestPhone.trim()}
                      >
                        {t("auth.continue")}
                      </Button>
                    </div>
                  </div>
                )}
                <div className={showPhonePrompt ? "hidden" : "space-y-3 pt-1"}>
                  <ApplePayCheckout
                    mode="cart"
                    requiresTokenizedPayment={requiresTokenizedPayment}
                    cartDonorMobile={guestPhone || undefined}
                    onSuccess={handlePaymentSuccess}
                    onLoginRequired={() => setShowPhonePrompt(true)}
                    triggerRef={applePayTriggerRef}
                    className="w-full"
                  />

                  <Button
                    type="button"
                    onClick={handleCompleteDonation}
                    disabled={isLoading || belowMinTotal}
                    className="w-full h-12 sm:h-14 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base sm:text-lg inline-flex items-center justify-center gap-2 sm:gap-3 shadow-none"
                  >
                    <CircleDollarToSlotIcon
                      className="size-5 shrink-0"
                      aria-hidden
                    />
                    {t("checkout.completeDonation")}
                  </Button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Unified Donation Modal (cart mode – requires login) */}
      <UnifiedDonationModal
        isOpen={donationModalOpen}
        onClose={handleCloseDonationModal}
        onSuccess={handlePaymentSuccess}
        cartMode
        guestPhoneOnlyApplePay={
          donationModalIntent === "applePayGuestPhoneOnly"
        }
        preparedPayment={null}
      />
    </div>
  );
}
